/**
 * KAN-826: contacts (client cards) of a company: name (link to the client
 * card), role, phone, Telegram, funnel stage, lead temperature and the row
 * actions "Карточка" / "Переписка" shared with "Лиды по теплоте".
 */
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LeadRowActions } from '@/components/leads/LeadRowActions';
import { LeadTemperatureBadge } from '@/components/leads/LeadTemperatureBadge';
import type { ClientCompanyContact } from '@/types/clientCompany';

interface ClientCompanyContactsCardProps {
  contacts: ClientCompanyContact[];
}

const TH = 'whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500';
const TD = 'px-3 py-2 align-top';

function contactName(contact: ClientCompanyContact): string {
  return (contact.name || '').trim() || 'Без имени';
}

export function ClientCompanyContactsCard({ contacts }: ClientCompanyContactsCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Контакты</CardTitle>
      </CardHeader>
      <CardContent>
        {contacts.length === 0 ? (
          <p data-testid="client-company-contacts-empty" className="py-4 text-center text-sm text-gray-500">
            У организации нет контактов
          </p>
        ) : (
          <div className="-mx-2 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-gray-200">
                <tr>
                  <th scope="col" className={TH}>Имя</th>
                  <th scope="col" className={TH}>Роль</th>
                  <th scope="col" className={TH}>Телефон</th>
                  <th scope="col" className={TH}>Telegram</th>
                  <th scope="col" className={TH}>Этап</th>
                  <th scope="col" className={TH}>Теплота</th>
                  <th scope="col" className={TH}>
                    <span className="sr-only">Действия</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => {
                  const name = contactName(contact);
                  const telegram = (contact.telegram || '').replace(/^@/, '');
                  return (
                    <tr key={contact.id} data-testid="client-company-contact" className="border-b border-gray-100 last:border-0">
                      <td className={`${TD} min-w-[140px]`}>
                        <Link to={`/clients/${contact.id}`} className="font-medium text-blue-700 hover:underline">
                          {name}
                        </Link>
                      </td>
                      <td className={TD}>{contact.role}</td>
                      <td className={`${TD} whitespace-nowrap`}>{contact.phone}</td>
                      <td className={`${TD} whitespace-nowrap`}>{telegram ? `@${telegram}` : ''}</td>
                      <td className={TD}>{contact.stage?.label ?? ''}</td>
                      <td className={TD}>
                        <LeadTemperatureBadge level={contact.temperature} />
                      </td>
                      <td className={TD}>
                        <LeadRowActions clientId={contact.id} name={name} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
